import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import StickyBottomNav from '../../components/Navbar/StickyBottomNav';
import { ArrowLeft, ChevronLeft, ChevronRight, AlertCircle, Download } from 'lucide-react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { useWindowWidth } from '@react-hook/window-size';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const IndividualNewsletter = () => {
  const { newsletterName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null);
  const windowWidth = useWindowWidth();
  const pdfWidth = Math.min(windowWidth * 0.9, 800);
  const [isCurrentPageLoading, setIsCurrentPageLoading] = useState(true);
  const [loadedPages, setLoadedPages] = useState(new Set());
  const [error, setError] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const goToPrevPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
  const goBack = () => navigate("/gallery/newsletters");

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        goToPrevPage();
      } else if (event.key === 'ArrowRight') {
        goToNextPage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pageNumber, numPages]);

  useEffect(() => {
    if (location.state?.pdfId) {
      setPdfUrl(`${apiUrl}/api/image/${location.state.pdfId}`);
    } else {
      setError("PDF not found. Please go back to the newsletters page.");
    }
  }, [location.state, apiUrl]);

  const preloadPage = (pageNum) => {
    if (pageNum <= numPages && pageNum > 0 && !loadedPages.has(pageNum)) {
      setLoadedPages(prev => new Set([...prev, pageNum]));
    }
  };

  useEffect(() => {
    if (numPages) {
      preloadPage(pageNumber + 1);
      preloadPage(pageNumber - 1);
    }
  }, [pageNumber, numPages]);

  const LoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-6"></div>
      <p className="text-textPrimary">Loading page...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />
      
      <div className="py-16 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <motion.button
              onClick={goBack}
              className="bg-primary text-white py-2 px-6 rounded-lg flex items-center shadow-sm"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Newsletters
            </motion.button>
          </div>
          
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-h1 text-secondary mb-4">
              {newsletterName}
            </h1>
            <div className="w-16 h-1 bg-cta mx-auto"></div>
          </motion.div>
          
          {error ? (
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-md max-w-md mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle size={32} className="text-red-500" />
              </div>
              <h3 className="font-display text-h3 text-secondary mb-2">Something went wrong</h3>
              <p className="text-textPrimary mb-6">{error}</p>
              <motion.button
                onClick={goBack}
                className="bg-primary text-white py-2 px-6 rounded-lg flex items-center mx-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeft size={20} className="mr-2" />
                Return to Newsletters
              </motion.button>
            </motion.div>
          ) : (
            <motion.div 
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* PDF Viewer */}
              <div className="bg-white rounded-xl p-6 shadow-md mb-6 w-full max-w-4xl">
                <div className="flex justify-center items-center mb-4 space-x-4">
                  <motion.button
                    onClick={goToPrevPage}
                    disabled={pageNumber <= 1 || isCurrentPageLoading}
                    className={`rounded-full w-10 h-10 flex items-center justify-center transition-colors ${
                      pageNumber <= 1 || isCurrentPageLoading 
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                    whileHover={pageNumber > 1 && !isCurrentPageLoading ? { scale: 1.1 } : {}}
                    whileTap={pageNumber > 1 && !isCurrentPageLoading ? { scale: 0.9 } : {}}
                  >
                    <ChevronLeft size={24} />
                  </motion.button>
                  
                  <span className="font-display text-h3 text-secondary">
                    Page {pageNumber} of {numPages || '?'}
                  </span>
                  
                  <motion.button
                    onClick={goToNextPage}
                    disabled={!numPages || pageNumber >= numPages || isCurrentPageLoading}
                    className={`rounded-full w-10 h-10 flex items-center justify-center transition-colors ${
                      !numPages || pageNumber >= numPages || isCurrentPageLoading 
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                    whileHover={numPages && pageNumber < numPages && !isCurrentPageLoading ? { scale: 1.1 } : {}}
                    whileTap={numPages && pageNumber < numPages && !isCurrentPageLoading ? { scale: 0.9 } : {}}
                  >
                    <ChevronRight size={24} />
                  </motion.button>
                </div>
                
                <div className="flex justify-center mb-4">
                  {pdfUrl && (
                    <a 
                      href={pdfUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center text-primary hover:underline"
                      download={`${newsletterName}.pdf`}
                    >
                      <Download size={16} className="mr-1" />
                      Download PDF
                    </a>
                  )}
                </div>
                
                <div className="pdf-container flex justify-center bg-gray-100 rounded-lg overflow-hidden p-2">
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={({ numPages }) => {
                      setNumPages(numPages);
                      setPageNumber(1);
                      setLoadedPages(new Set([1]));
                    }}
                    loading={<LoadingSpinner />}
                    onLoadError={(error) => {
                      console.error('Error while loading PDF:', error);
                      setError("Failed to load the newsletter. Please try again later.");
                    }}
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={pdfWidth}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading={<LoadingSpinner />}
                      onLoadSuccess={() => setIsCurrentPageLoading(false)}
                      onRenderSuccess={() => setIsCurrentPageLoading(false)}
                      onLoadStart={() => setIsCurrentPageLoading(true)}
                      className="shadow-md"
                    />
                    
                    {/* Preload adjacent pages */}
                    {pageNumber < numPages && (
                      <div className="hidden">
                        <Page
                          pageNumber={pageNumber + 1}
                          width={pdfWidth}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </div>
                    )}
                    
                    {pageNumber > 1 && (
                      <div className="hidden">
                        <Page
                          pageNumber={pageNumber - 1}
                          width={pdfWidth}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </div>
                    )}
                  </Document>
                </div>
                
                <div className="flex justify-center items-center mt-4 space-x-4">
                  <motion.button
                    onClick={goToPrevPage}
                    disabled={pageNumber <= 1 || isCurrentPageLoading}
                    className={`rounded-full w-10 h-10 flex items-center justify-center transition-colors ${
                      pageNumber <= 1 || isCurrentPageLoading 
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                    whileHover={pageNumber > 1 && !isCurrentPageLoading ? { scale: 1.1 } : {}}
                    whileTap={pageNumber > 1 && !isCurrentPageLoading ? { scale: 0.9 } : {}}
                  >
                    <ChevronLeft size={24} />
                  </motion.button>
                  
                  <span className="font-display text-h3 text-secondary">
                    Page {pageNumber} of {numPages || '?'}
                  </span>
                  
                  <motion.button
                    onClick={goToNextPage}
                    disabled={!numPages || pageNumber >= numPages || isCurrentPageLoading}
                    className={`rounded-full w-10 h-10 flex items-center justify-center transition-colors ${
                      !numPages || pageNumber >= numPages || isCurrentPageLoading 
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                    whileHover={numPages && pageNumber < numPages && !isCurrentPageLoading ? { scale: 1.1 } : {}}
                    whileTap={numPages && pageNumber < numPages && !isCurrentPageLoading ? { scale: 0.9 } : {}}
                  >
                    <ChevronRight size={24} />
                  </motion.button>
                </div>
              </div>
              
              <p className="text-textPrimary text-center text-sm">
                Tip: You can also use keyboard arrow keys to navigate between pages.
              </p>
            </motion.div>
          )}
        </div>
      </div>
      
      <Footer />
      <StickyBottomNav />
    </div>
  );
};

export default IndividualNewsletter;